import {
    baseHttpRequestWithFetchAPI,
    httpRequest,
    type HttpRequestError,
} from '../../../server/client/httpRequest.no-deps'

import * as sentry from '../../src/utils/sentry'
import type { Request, RequestArgs, Route } from '../../../server/http/types.no-deps'
import { Cust_httpUrlGet } from '../Cust/Cust.get'
import { DS_getData } from '../DS/DS.Data'
import type { Cust_ICustomer } from '../Cust/Cust.ICustomer'
import { hasField } from '../../../shared/lib/functional/object.no-deps'
import { Logg_event } from '../Logg/Logg.event'
import { LS_accessTokenRemove } from '../LS/LS.accessToken'
import { Auth_setAsLoggedOut } from '../Auth/Auth.State'
import type { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'
import type { Dictionary } from '../../../shared/lib/functional/dictionary.no-deps'
import Errors from '../../../shared/domain/errors.no-deps'

export const Serv_httpRequest: Request = Serv_httpRequestWithOption({})

export interface Serv_HttpRequestOptions {
    accessToken?: string
    customer?: Cust_ICustomer
    logger?: ClientLogger
}

export function Serv_httpRequestWithOption(options: Serv_HttpRequestOptions): Request {
    return async (route, requestArgs) => {
        let method: string = route.method

        if (hasField(requestArgs, 'params') && hasField(requestArgs.params, 'method')) {
            method = requestArgs.params.method as string
        }

        let tags: Dictionary<string> = { method, pattern: route.pattern }
        let errorExtra: Dictionary<unknown> = { route, requestArgs }

        try {
            // If options are not passed, we want to get accessToken & customer before make http request
            const token = options.accessToken ?? DS_getData().accessToken
            const customer = options.customer ?? DS_getData().customer
            if (token == null) throw new Error('cannot use httpClient unless logged in')

            options.logger?.log('http request sent', {}, tags)

            const response = await httpRequest({
                token,
                baseUrl: Cust_httpUrlGet(customer),
                route,
                requestArgs,
                customerSlug: customer?.slug,
                logGroupId: options.logger?.logGroupId,
                logGroupName: options.logger?.logGroupName,
            }).catch((error: HttpRequestError) => {
                const errors = error.response?.body?.errors

                tags = { ...tags, status: error.status.toString() }
                errorExtra = { ...errorExtra, errors }

                // checking invalid session
                if (error.status === 401 || error.message.substring(0, 12) === '[ERR 000001]') {
                    Logg_event('InvalidToken', {})
                    // Show an error message to the user explaining that they've
                    // been logged out for security reasons
                    Auth_setAsLoggedOut()
                    // Remove access token
                    LS_accessTokenRemove()
                }

                throw error
            })

            options.logger?.log('http request success', {}, tags)

            return response
        } catch (error) {
            // If not expecting an error, send it to sentry
            if (shouldSentry(route, method, error.message)) {
                if (options.logger) {
                    options.logger.error(error, errorExtra, tags)
                } else {
                    sentry.err(error, { extra: errorExtra, tags })
                }
            }

            throw error
        }
    }
}

/**
 * Fetches content from the API server using the browser's Fetch API.
 *
 * Useful for consuming streamed content as Superagent in the browser doesn't seem to
 * allow listening to 'data' events to consume data as they arrive, it will instead buffer the
 * response until it's fully downloaded before raising the `response` event.
 */
export function Serv_baseHttpRequestWithFetchAPI(options: Serv_HttpRequestOptions) {
    return (
        route: Route,
        requestArgs: RequestArgs<any>
    ): { responsePromise: Promise<Response>; controller: AbortController } => {
        // If options are not passed, we want to get accessToken & customer before make http request
        const token = options.accessToken ?? DS_getData().accessToken
        const customer = options.customer ?? DS_getData().customer

        if (!token) {
            throw new Error('Cannot use httpClient unless logged in')
        }

        if (!customer) {
            throw new Error('Cannot use httpClient without a customer')
        }

        return baseHttpRequestWithFetchAPI({
            token,
            baseUrl: Cust_httpUrlGet(customer),
            route,
            requestArgs,
            customerSlug: customer?.slug,
            logGroupId: options.logger?.logGroupId,
            logGroupName: options.logger?.logGroupName,
        })
    }
}

function shouldSentry(_route: Route, method: string, message: string) {
    if (method === 'invites.recommendUser') return false
    if (message === 'Server is busy') return false
    if (message === Errors.report.alreadyComplete) return false
    if (message === Errors.report.hasBeenCancelled) return false
    if (message === Errors.report.notReportCollaborator) return false
    if (message === Errors.report.reportIsIncomplete) return false
    if (message === Errors.report.reportIsInvalid) return false
    return true
}
